"use client";
import { useState } from "react";
import Link from "next/link";

// ── Data ──────────────────────────────────────────────────────────────────────
const freeZones = [
  {
    slug: "dubai-south",
    href: "/free-zone/locations/adgm",
    name: "Dubai South Free Zone",
    fullName: "Dubai South Free Zone",
    location: "Dubai",
    price: "AED 4,500",
    desc: "A modern, diversified free zone with state-of-the-art facilities and logistics infrastructure. Ideal for trading, manufacturing, and supply chain operations with competitive pricing and streamlined business setup services.",
    tag: "Trade & Logistics",
  },
  {
    slug: "meydan",
    href: "/free-zone/locations/dafza",
    name: "Meydan Free Zone",
    fullName: "Meydan Free Zone Dubai",
    location: "Dubai",
    price: "AED 5,800",
    desc: "A premier logistics hub in Dubai offering world-class facilities for trading, warehousing, and distribution. Perfect for businesses seeking connectivity, modern infrastructure, and efficient customs clearance processes.",
    tag: "Logistics",
  },
  {
    slug: "rakez",
    href: "/free-zone/locations/dcc",
    name: "RAKEZ",
    fullName: "RAKEZ - Ras Al Khaimah Economic Zone",
    location: "Ras Al Khaimah",
    price: "AED 3,500",
    desc: "One of the UAE's most cost-effective free zones offering comprehensive business setup solutions. Ideal for service providers, traders, and professionals seeking affordable licensing and residency visa services.",
    tag: "Multi-Sector",
  },
  {
    slug: "ajman-free-zone",
    href: "/free-zone/locations/ajman",
    name: "Ajman Free Zone",
    fullName: "Ajman Free Zone",
    location: "Ajman",
    price: "AED 5,555",
    desc: "A dynamic and cost-effective ecosystem with fully digitized setup processes and strategic proximity to Ajman Port and major UAE airports. Supporting traders, manufacturers, and service providers.",
    tag: "Trade",
  },
  {
    slug: "nu-venture",
    href: "/free-zone/locations/dcc",
    name: "Nu Venture Ajman",
    fullName: "Nu Venture Ajman Free Zone",
    location: "Ajman",
    price: "AED 4,200",
    desc: "An innovative business hub offering flexible licensing options for startups and established enterprises. Renowned for efficient company formation, residency visa support, and business setup assistance.",
    tag: "Startups",
  },
  {
    slug: "shams",
    href: "free-zone/locations/d3",
    name: "Sharjah Media City",
    fullName: "Sharjah Media City (Shams) Free Zone",
    location: "Sharjah",
    price: "AED 6,200",
    desc: "A specialized free zone for media, digital, and creative industries. Provides comprehensive business setup services, residency visa solutions, and license processing for creative professionals and digital enterprises.",
    tag: "Creative & Media",
  },
  {
    slug: "uaq-ftz",
    href: "/free-zone/locations/adgm",
    name: "UAQ FTZ",
    fullName: "Umm Al Quwain Free Trade Zone",
    location: "Umm Al Quwain",
    price: "AED 3,800",
    desc: "An emerging free trade zone offering competitive pricing and comprehensive business support services. Excellent for traders, service providers, and professionals seeking affordable company formation and visa assistance.",
    tag: "Trade",
  },
];

const nationalities = [
  "Afghan", "Albanian", "Algerian", "American", "Argentinian", "Australian", "Austrian",
  "Bangladeshi", "Belgian", "Brazilian", "British", "Canadian", "Chinese", "Colombian",
  "Danish", "Dutch", "Egyptian", "Emirati", "Ethiopian", "Filipino", "Finnish", "French",
  "German", "Ghanaian", "Greek", "Indian", "Indonesian", "Iranian", "Iraqi", "Irish",
  "Italian", "Japanese", "Jordanian", "Kenyan", "Korean", "Lebanese", "Malaysian",
  "Mexican", "Moroccan", "Nigerian", "Norwegian", "Pakistani", "Polish", "Portuguese",
  "Romanian", "Russian", "Saudi", "South African", "Spanish", "Sri Lankan", "Swedish",
  "Swiss", "Syrian", "Thai", "Turkish", "Ukrainian", "Vietnamese", "Yemeni", "Other",
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function FreeZoneSection() {
  const [form, setForm] = useState({
    fname: "", lname: "", email: "", phone: "", nationality: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setForm({ fname: "", lname: "", email: "", phone: "", nationality: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    }, 900);
  };

  return (
    <section className="w-full bg-[#FAFAFA] py-16 px-4 sm:px-6 lg:px-12">
      <div className="max-w-[2400px] mx-auto">

        {/* ── Page header ─────────────────────────────────── */}
        <div className="mb-10 px-4 md:px-0">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#B8893A] mb-3">
            Free Zones
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111] leading-tight mb-4">
            List of top UAE free zones<br className="hidden sm:block" /> with pricing
          </h1>
          <p className="text-[#444] text-base max-w-3xl leading-relaxed">
            With more than 40 free zones in the UAE, choosing the right one can be challenging. Our company formation experts will guide you to the free zone that best supports your business activities and goals.

          </p>
        </div>

        {/* ── Two-column layout ───────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* LEFT — scrollable list */}
          <div className="w-full lg:flex-1 flex flex-col gap-5">
            {freeZones.map((zone) => (
              <ZoneCard key={zone.slug} zone={zone} />
            ))}
          </div>

          {/* RIGHT — sticky contact card */}
          <div className="w-full lg:w-[340px] xl:w-[500px] flex-shrink-0 lg:sticky lg:top-17">
            <div className="rounded-2xl border border-gray-200 bg-white p-7 sm:p-8 shadow-xl transition-all duration-200">
              <h2 className="text-xl font-bold text-[#111] mb-1">
                Discover Your Ideal Free Zone
              </h2>
              <p className="text-sm text-[#666] mb-6 leading-relaxed">
                As a leading channel partner, we offer complete business setup assistance including company formation, residency visas, and license processing across all listed free zones.
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">

                {/* First + Last */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name" required>
                    <input
                      name="fname" type="text" value={form.fname}
                      onChange={handleChange} placeholder="John"
                      style={inputSt} required
                    />
                  </Field>
                  <Field label="Last Name" required>
                    <input
                      name="lname" type="text" value={form.lname}
                      onChange={handleChange} placeholder="Doe"
                      style={inputSt} required
                    />
                  </Field>
                </div>

                {/* Email */}
                <Field label="Email" required>
                  <input
                    name="email" type="email" value={form.email}
                    onChange={handleChange} placeholder="email@example.com"
                    style={inputSt} required
                  />
                </Field>

                {/* Phone */}
                <Field label="Phone" required>
                  <div className="flex gap-2">
                    <span
                      className="flex items-center gap-1.5 px-3 text-sm text-[#333] border border-[#E5E7EB] rounded-lg flex-shrink-0"
                      style={{ background: "#F7F7F7", height: "44px" }}
                    >
                      🇦🇪 +971
                    </span>
                    <input
                      name="phone" type="tel" value={form.phone}
                      onChange={handleChange} placeholder="50 123 4567"
                      style={{ ...inputSt, flex: 1 }} required
                    />
                  </div>
                </Field>

                {/* Nationality */}
                <Field label="Nationality" required>
                  <select
                    name="nationality" value={form.nationality}
                    onChange={handleChange}
                    style={{ ...inputSt, cursor: "pointer" }}
                    required
                  >
                    <option value="" disabled>Select</option>
                    {nationalities.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </Field>

                {/* Message */}
                <Field label="Message">
                  <textarea
                    name="message" value={form.message}
                    onChange={handleChange} placeholder="Message"
                    rows={4}
                    style={{ ...inputSt, resize: "vertical", paddingTop: "12px", minHeight: "100px" }}
                  />
                </Field>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg py-3 text-sm font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
                  style={{ background: "#B8893A" }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#A67B34"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#B8893A"; }}
                >
                  {loading ? "Sending…" : "Book Free Consultation"}
                </button>

                {submitted && (
                  <p className="text-center text-sm font-medium text-green-600">
                    ✓ We&apos;ll be in touch soon!
                  </p>
                )}
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ── Zone Card ─────────────────────────────────────────────────────────────────
function ZoneCard({ zone }) {
  return (
    <div className="rounded-2xl border border-[#E8E8E8] bg-[#FAFAFA] p-6 transition-all duration-200 hover:border-[#B8893A] hover:bg-white group">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <span className="inline-block text-[10px] font-semibold tracking-widest uppercase text-[#B8893A] bg-[#FEF3C7] rounded px-2 py-0.5 mb-2">
            {zone.tag}
          </span>
          <h3 className="text-lg font-bold text-[#111] leading-snug group-hover:text-[#B8893A] transition-colors duration-200">
            {zone.fullName}
          </h3>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-[#888] mb-0.5">Starting from</p>
          <p className="text-base font-bold text-[#111]">{zone.price}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-[#666] mb-3">
        <span className="flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
          {zone.location}
        </span>
        <span className="flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
          License available
        </span>
      </div>

      <p className="text-sm text-[#555] leading-relaxed mb-4">{zone.desc}</p>

      {/* <Link
        href={zone.href}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B8893A] hover:gap-3 transition-all duration-200"
      >
        View Details
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
      </Link> */}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-[#111] tracking-wide">
        {label}{required && <span className="text-[#B8893A] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────
const inputSt = {
  height: "44px",
  background: "#F7F7F7",
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  padding: "0 14px",
  fontSize: "14px",
  color: "#111",
  width: "100%",
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color 0.15s",
};